/* Shopify owns prices and discounts. This display reads the applied allocations. */
(() => {
  const isStack = title => /stack/i.test(title || '') || /^Tier [123] reward$/i.test((title || '').trim());
  function summarize(cart, tiers = [10,20,30]) {
    const total = Math.max(0, Number(cart.original_total_price || 0) - Number(cart.total_price || 0));
    let stack = 0, appliedPercent = 0;
    const count = Number(cart.item_count || 0);
    const read = (app, amount) => {
      if (!isStack(app?.title)) return;
      stack += Number(amount || 0);
      if (app.value_type === 'percentage') appliedPercent = Math.max(appliedPercent, Number(app.value || 0));
    };
    (cart.cart_level_discount_applications || []).forEach(app => read(app, app.total_allocated_amount));
    (cart.items || []).forEach(item => {
      if (item.line_level_discount_allocations?.length) item.line_level_discount_allocations.forEach(a => read(a.discount_application, a.amount));
      else (item.discounts || []).forEach(d => read(d, d.amount));
    });
    stack = Math.min(total, Math.max(0, stack));
    const tier = count >= 4 ? 2 : count >= 3 ? 1 : count >= 2 ? 0 : -1;
    return { count, total, stack, other:total-stack, appliedPercent, tier, percent:tier >= 0 ? tiers[tier] : 0 };
  }
  if (typeof module !== 'undefined') module.exports = { summarize };
  if (typeof window === 'undefined') return;
  let mapPromise;
  const config = () => window.IllegalShirts || {};
  const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency',currency:window.Shopify?.currency?.active || 'USD'}).format(cents / 100);
  async function images(root = document) {
    const imgs = [...root.querySelectorAll('.cart-line-img img,.cart-line-img-wrap img')];
    if (!imgs.length) return;
    const c = config();
    if (!c.product_image_map || !c.product_asset_base) return;
    try {
      mapPromise ||= fetch(c.product_image_map).then(r => { if (!r.ok) throw Error('Image map unavailable'); return r.json(); }).catch(e => {mapPromise=null; throw e;});
      const map = await mapPromise;
      for (const img of imgs) {
        const original = img.getAttribute('src');
        const name = new URL(original,location.href).pathname.split('/').pop().replace(/_160x200(?=\.[^.]+$)/,'');
        if (!map[name] || !img.isConnected || img.getAttribute('src') !== original) continue;
        img.addEventListener('error',() => { img.src=original; },{once:true});
        img.src = new URL(map[name],new URL(c.product_asset_base,location.href)).href;
      }
    } catch (_) { /* Multiply blending preserves the gray well if no cutout exists. */ }
  }
  let lineRequest, lineVersion=0;
  async function linePrices(cart) {
    const version=++lineVersion;
    lineRequest?.abort(); lineRequest=new AbortController();
    try {
      const url=new URL(window.Shopify?.routes?.root || '/',location.origin);
      url.searchParams.set('section_id','cart-line-savings');
      const response=await fetch(url,{credentials:'same-origin',signal:lineRequest.signal});
      if(!response.ok) return;
      const doc=new DOMParser().parseFromString(await response.text(),'text/html');
      const data=JSON.parse(doc.querySelector('[data-cart-line-savings]')?.content.textContent || 'null');
      if(version!==lineVersion || !data?.lines) return;
      const items=new Map((cart.items||[]).map(i=>[i.key,i]));
      if(data.lines.length!==items.size || data.lines.some(line=>!items.has(line.key)||items.get(line.key).quantity!==line.quantity)) return;
      document.querySelectorAll('.cart-line-price').forEach(el=>{
        const key=el.closest('[data-key]')?.dataset.key, line=data.lines.find(i=>i.key===key);
        if(!line) return;
        el.innerHTML=line.stack>0?`<s class="cart-line-original">${money(line.original)}</s><strong>${money(line.final)}</strong><small class="cart-line-stack-label">Stack &amp; Save${line.percent?' · '+line.percent+'% off':''}<span>Saved ${money(line.stack)}</span></small>`:money(line.final);
      });
    } catch(_) { /* Current Shopify line prices remain visible if the allocation request fails. */ }
  }
  function render(cart) {
    const c=config(), s=summarize(cart,c.stack_tiers);
    document.querySelectorAll('[data-cart-stack-progress]').forEach(root => {
      root.hidden = !c.stack_enabled || !s.count;
      root.querySelector('[data-stack-active]').textContent = s.stack && s.appliedPercent ? `${s.appliedPercent}% applied` : s.percent ? `${s.percent}% tier` : '';
      const next = Math.min(2, s.tier + 1);
      root.querySelector('[data-stack-message]').textContent = s.count < 4 ? `Add ${Math.max(1,next+2-s.count)} more item${next+2-s.count > 1 ? 's' : ''} for ${c.stack_tiers[next]}% off.` : 'You’ve reached the highest tier.';
      root.querySelectorAll('[data-stack-step]').forEach(step => {
        const qty=Number(step.dataset.stackStep), reached=s.count>=qty;
        const start = qty === 2 ? 0 : qty - 1;
        const fill = Math.max(0, Math.min(100, (s.count - start) / (qty - start) * 100));
        step.style.setProperty('--stack-fill', `${fill}%`);
        step.classList.toggle('is-reached',reached);
        if (qty === Math.max(2,Math.min(4,s.count))) step.setAttribute('aria-current','step'); else step.removeAttribute('aria-current');
      });
    });
    document.querySelectorAll('[data-stack-savings-row]').forEach(row => {
      row.hidden = !s.stack;
      row.querySelector('[data-stack-savings-label]').textContent = `Stack & Save savings${s.appliedPercent ? ' ('+s.appliedPercent+'%)' : ''}`;
      row.querySelector('[data-stack-savings]').textContent = '−'+money(s.stack);
    });
    document.querySelectorAll('[data-other-savings-row]').forEach(row => {
      row.hidden=!s.other; row.querySelector('[data-other-savings]').textContent='−'+money(s.other);
    });
    const pageLabel=document.getElementById('cart-summary-discount-label');
    if (pageLabel) pageLabel.textContent=s.stack && !s.other ? `Stack & Save savings${s.appliedPercent ? ' ('+s.appliedPercent+'%)' : ''}` : 'Savings';
    const stickyMessage=document.querySelector('[data-stack-sticky-message]');
    if (stickyMessage) stickyMessage.textContent=s.count < 4 ? `Add ${s.count < 2 ? 2-s.count : 1} more ${s.count === 0 ? 'items' : 'item'} for ${c.stack_tiers[Math.min(2,s.tier+1)]}% off` : `${s.count} items · ${s.appliedPercent || s.percent}% off${s.stack ? ' applied' : ' tier'}`;
    images();
    linePrices(cart);
  }
  window.CartRewards={render,images};
  const start=()=>{try {render(JSON.parse(document.getElementById('cart-rewards-seed').textContent));} catch (_) {}};
  if (document.readyState==='loading') document.addEventListener('DOMContentLoaded',start); else start();
})();
